import { useLayoutEffect, useRef, useState } from "react";
import type { RefObject } from "react";

function clamp01(v: number): number {
    return Math.min(1, Math.max(0, v));
}

export function computeDocumentSectionScrollProgress(
    section: HTMLElement
): number {
    const trackH = section.offsetHeight;
    const viewH = window.innerHeight;
    const scrollable = trackH - viewH;
    const rect = section.getBoundingClientRect();

    if (scrollable <= 0) {
        if (rect.bottom <= 0) {
            return 1;
        }
        if (rect.top >= viewH) {
            return 0;
        }
        const past = Math.min(Math.max(-rect.top, 0), trackH);
        return trackH <= 0 ? 0 : clamp01(past / trackH);
    }

    const scrolled = Math.min(Math.max(-rect.top, 0), scrollable);
    return clamp01(scrolled / scrollable);
}

export function useSpiderwebDocumentSectionProgress(
    sectionRef: RefObject<HTMLElement | null> | undefined,
    enabled: boolean
): { progress: number; isAttached: boolean } {
    const [progress, setProgress] = useState(0);
    const [isAttached, setIsAttached] = useState(false);
    const isAttachedRef = useRef(false);

    useLayoutEffect(() => {
        if (!enabled || !sectionRef) {
            setProgress(0);
            if (isAttachedRef.current) {
                isAttachedRef.current = false;
                setIsAttached(false);
            }
            return;
        }

        let detach: (() => void) | undefined;
        let rafId = 0;
        let cancelled = false;

        const attach = (target: HTMLElement) => {
            const update = () => {
                if (!cancelled) {
                    setProgress(computeDocumentSectionScrollProgress(target));
                }
            };
            update();
            isAttachedRef.current = true;
            setIsAttached(true);

            window.addEventListener("scroll", update, { passive: true });
            window.addEventListener("resize", update);
            const ro = new ResizeObserver(update);
            ro.observe(target);

            detach = () => {
                window.removeEventListener("scroll", update);
                window.removeEventListener("resize", update);
                ro.disconnect();
                if (!cancelled) {
                    isAttachedRef.current = false;
                    setIsAttached(false);
                    setProgress(0);
                }
            };
        };

        const el = sectionRef.current;
        if (el) {
            attach(el);
            return () => {
                cancelled = true;
                detach?.();
            };
        }

        const poll = () => {
            if (cancelled) {
                return;
            }
            const node = sectionRef.current;
            if (node) {
                attach(node);
                return;
            }
            rafId = requestAnimationFrame(poll);
        };
        rafId = requestAnimationFrame(poll);

        return () => {
            cancelled = true;
            cancelAnimationFrame(rafId);
            detach?.();
        };
    }, [enabled, sectionRef]);

    return { progress, isAttached };
}
